import React, { useState } from 'react';
import { MapPin } from 'lucide-react';

/**
 * Global Presence map, clean, professional SVG world map with 6 hub markers.
 * Pure SVG + CSS, no external dependencies. Multilingual labels (EN/ES/RU).
 *
 * Coordinate system: 1000×500 viewBox following equirectangular projection.
 *   x = (longitude + 180) / 360 * 1000
 *   y = (90 - latitude)  / 180 * 500
 *
 * `labelAnchor` per city pushes labels in different directions so they never
 * overlap (e.g. London + Madrid are only 20 px apart on the Y axis).
 */
const CITIES = [
  // labelAnchor uses x,y deltas in viewBox units relative to the marker.
  { key: 'madrid',    en: { name: 'Madrid',       country: 'Spain' },          es: { name: 'Madrid',         country: 'España' },          ru: { name: 'Мадрид',           country: 'Испания' },           x: 494, y: 141, labelDx:  16, labelDy:  18, anchor: 'start' },
  { key: 'london',    en: { name: 'London',       country: 'United Kingdom' }, es: { name: 'Londres',        country: 'Reino Unido' },     ru: { name: 'Лондон',           country: 'Великобритания' },    x: 498, y: 121, labelDx: -10, labelDy: -14, anchor: 'end'   },
  { key: 'mexico',    en: { name: 'Mexico City',  country: 'Mexico' },         es: { name: 'Ciudad de México', country: 'México' },         ru: { name: 'Мехико',           country: 'Мексика' },           x: 225, y: 220, labelDx: -10, labelDy:  20, anchor: 'end'   },
  { key: 'miami',     en: { name: 'Miami',        country: 'United States' },  es: { name: 'Miami',          country: 'Estados Unidos' },  ru: { name: 'Майами',           country: 'США' },               x: 276, y: 198, labelDx:  16, labelDy: -10, anchor: 'start' },
  { key: 'dubai',     en: { name: 'Dubai',        country: 'UAE' },            es: { name: 'Dubái',          country: 'EAU' },             ru: { name: 'Дубай',            country: 'ОАЭ' },               x: 656, y: 182, labelDx:  16, labelDy:   6, anchor: 'start' },
  { key: 'singapore', en: { name: 'Singapore',    country: 'Asia' },           es: { name: 'Singapur',       country: 'Asia' },            ru: { name: 'Сингапур',         country: 'Азия' },              x: 793, y: 263, labelDx:  16, labelDy:  18, anchor: 'start' },
];

const T = {
  en: {
    heading: 'Global Presence',
    kicker: 'Worldwide',
    tagline: 'Trusted hubs serving qualified laboratories and licensed professionals on six continents.',
  },
  es: {
    heading: 'Presencia Global',
    kicker: 'A nivel mundial',
    tagline: 'Centros de confianza que atienden a laboratorios cualificados y profesionales autorizados en seis continentes.',
  },
  ru: {
    heading: 'Глобальное присутствие',
    kicker: 'По всему миру',
    tagline: 'Надёжные хабы, обслуживающие сертифицированные лаборатории и лицензированных специалистов на шести континентах.',
  },
};

const GlobalPresenceMap = ({ currentLanguage = 'en' }) => {
  const t = T[currentLanguage] || T.en;
  const [hoverCity, setHoverCity] = useState(null);
  const labelLang = ['en', 'es', 'ru'].includes(currentLanguage) ? currentLanguage : 'en';

  return (
    <section
      className="relative py-16 md:py-24 bg-gradient-to-br from-[#0b1220] via-[#0f1f2d] to-[#0d3a3a] overflow-hidden"
      data-testid="global-presence-section"
    >
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-capsule-aqua text-xs uppercase tracking-widest font-medium mb-4">
            <MapPin className="w-3.5 h-3.5" /> {t.kicker}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">
            {t.heading}
          </h2>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            {t.tagline}
          </p>
        </div>

        <div className="relative rounded-3xl overflow-hidden bg-white/[0.03] border border-white/10 backdrop-blur-sm p-3 md:p-6">
          <svg
            viewBox="0 0 1000 500"
            className="w-full h-auto"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={t.heading}
          >
            <defs>
              <radialGradient id="markerGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%"   stopColor="#22D3EE" stopOpacity="0.9" />
                <stop offset="60%"  stopColor="#14B8A6" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#0D9488" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="oceanFade" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#0e2233" stopOpacity="0" />
                <stop offset="100%" stopColor="#0b1220" stopOpacity="0.5" />
              </linearGradient>
            </defs>

            {/* Soft graticule (every 30°) for subtle depth */}
            <g stroke="rgba(148,163,184,0.05)" strokeWidth="0.4">
              {[60, 120, 180, 240, 300, 360, 420, 480].map(y => (
                <line key={`h${y}`} x1="0" y1={y} x2="1000" y2={y} />
              ))}
              {[125, 250, 375, 500, 625, 750, 875].map(x => (
                <line key={`v${x}`} x1={x} y1="20" x2={x} y2="480" />
              ))}
            </g>

            {/* Subtle ocean-floor fade */}
            <rect x="0" y="0" width="1000" height="500" fill="url(#oceanFade)" pointerEvents="none" />

            {/* World landmasses, abstracted but clean continent shapes */}
            <g
              fill="rgba(94,234,212,0.10)"
              stroke="rgba(94,234,212,0.45)"
              strokeWidth="0.7"
              strokeLinejoin="round"
            >
              {/* North America */}
              <path d="M120,90 Q170,75 220,80 Q275,90 310,135 Q325,175 305,210 Q280,235 250,235 Q220,245 195,225 Q165,210 145,180 Q125,155 120,120 Z" />
              {/* Greenland */}
              <path d="M340,75 Q370,68 388,90 Q392,115 372,128 Q352,128 342,108 Z" />
              {/* Central America */}
              <path d="M255,225 Q275,225 290,238 Q295,250 285,258 Q272,258 262,248 Z" />
              {/* South America */}
              <path d="M280,260 Q310,260 325,300 Q335,355 315,395 Q295,425 285,408 Q270,375 272,330 Q275,290 280,260 Z" />
              {/* Europe */}
              <path d="M460,108 Q500,98 540,108 Q565,128 555,155 Q525,170 488,162 Q468,150 460,128 Z" />
              {/* Scandinavia */}
              <path d="M515,75 Q540,68 555,90 Q548,108 525,108 Q510,98 515,75 Z" />
              {/* Africa */}
              <path d="M490,170 Q540,170 568,205 Q580,260 555,310 Q525,348 500,335 Q478,295 478,235 Q478,200 490,170 Z" />
              {/* Middle East */}
              <path d="M570,135 Q620,128 660,148 Q670,180 645,200 Q610,205 585,185 Q565,165 570,135 Z" />
              {/* Russia / Northern Asia */}
              <path d="M580,80 Q700,68 850,80 Q900,110 870,140 Q780,150 690,140 Q610,135 580,115 Z" />
              {/* India / South Asia */}
              <path d="M700,180 Q735,180 745,210 Q745,235 720,245 Q695,235 690,210 Z" />
              {/* East Asia / China */}
              <path d="M755,150 Q820,150 850,180 Q840,215 800,225 Q765,220 750,195 Q745,170 755,150 Z" />
              {/* SE Asia / Indonesia */}
              <path d="M770,255 Q815,250 840,275 Q825,295 790,295 Q768,285 770,255 Z" />
              {/* Australia */}
              <path d="M820,335 Q872,330 900,358 Q890,392 850,396 Q815,385 810,360 Z" />
              {/* New Zealand */}
              <path d="M915,395 Q930,392 935,408 Q925,418 912,415 Z" />
              {/* Japan */}
              <path d="M870,160 Q882,158 887,178 Q880,188 868,185 Q865,170 870,160 Z" />
              {/* UK Isles */}
              <path d="M488,115 Q500,113 502,128 Q495,135 484,131 Z" />
            </g>

            {/* Connection lines between hubs (subtle) */}
            <g stroke="rgba(34,211,238,0.18)" strokeWidth="0.7" fill="none" strokeDasharray="2 4">
              {(() => {
                const pairs = [
                  ['madrid', 'london'], ['madrid', 'miami'], ['madrid', 'dubai'],
                  ['miami', 'mexico'], ['dubai', 'singapore'], ['london', 'miami'],
                ];
                const idx = Object.fromEntries(CITIES.map(c => [c.key, c]));
                return pairs.map(([a, b]) => {
                  const A = idx[a]; const B = idx[b];
                  const mx = (A.x + B.x) / 2;
                  const my = Math.min(A.y, B.y) - Math.abs(B.x - A.x) * 0.18;
                  return <path key={`${a}-${b}`} d={`M${A.x},${A.y} Q${mx},${my} ${B.x},${B.y}`} />;
                });
              })()}
            </g>

            {/* Markers (SVG), bigger pins + animated halo */}
            {CITIES.map((c, i) => {
              const isHover = hoverCity === c.key;
              return (
                <g
                  key={c.key}
                  data-testid={`map-marker-${c.key}`}
                  onMouseEnter={() => setHoverCity(c.key)}
                  onMouseLeave={() => setHoverCity(null)}
                  onFocus={() => setHoverCity(c.key)}
                  onBlur={() => setHoverCity(null)}
                  tabIndex={0}
                  style={{ cursor: 'pointer', outline: 'none' }}
                >
                  {/* Pulsing halo */}
                  <circle cx={c.x} cy={c.y} r="22" fill="url(#markerGlow)">
                    <animate attributeName="r"      values="18;30;18" dur="2.6s" begin={`${i * 0.22}s`} repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.9;0.2;0.9" dur="2.6s" begin={`${i * 0.22}s`} repeatCount="indefinite" />
                  </circle>
                  {/* Click target (invisible, larger for mobile taps) */}
                  <circle cx={c.x} cy={c.y} r="14" fill="transparent" />
                  {/* Solid pin */}
                  <circle cx={c.x} cy={c.y} r={isHover ? 6.5 : 5.5} fill="#22D3EE" stroke="#0b1220" strokeWidth="1.2" />
                  <circle cx={c.x} cy={c.y} r="2.2" fill="#ffffff" />

                  {/* Localized label */}
                  <g transform={`translate(${c.x + c.labelDx}, ${c.y + c.labelDy})`} pointerEvents="none">
                    {/* Background pill, auto-sized using textLength would require runtime measurement.
                        We instead use a subtle dark drop-shadow + halo on the text itself. */}
                    <text
                      textAnchor={c.anchor}
                      dominantBaseline="middle"
                      fontSize="13"
                      fontWeight="700"
                      fill="#ffffff"
                      stroke="#0b1220"
                      strokeWidth="3"
                      paintOrder="stroke"
                      data-testid={`map-label-${c.key}`}
                    >
                      {c[labelLang].name}
                    </text>
                    <text
                      textAnchor={c.anchor}
                      dominantBaseline="hanging"
                      fontSize="9"
                      fontWeight="500"
                      fill="rgba(255,255,255,0.7)"
                      stroke="#0b1220"
                      strokeWidth="2.5"
                      paintOrder="stroke"
                      y="9"
                    >
                      {c[labelLang].country}
                    </text>
                  </g>
                </g>
              );
            })}

            {/* Hover tooltip */}
            {hoverCity && (() => {
              const c = CITIES.find(x => x.key === hoverCity);
              if (!c) return null;
              const tx = Math.min(Math.max(c.x, 100), 880);
              const ty = c.y - 38;
              return (
                <g pointerEvents="none">
                  <rect x={tx - 70} y={ty - 14} width="140" height="28" rx="6" fill="#0b1220" stroke="#22D3EE" strokeWidth="0.8" opacity="0.95" />
                  <text x={tx} y={ty + 5} textAnchor="middle" fontSize="11" fontWeight="700" fill="#ffffff">
                    {c[labelLang].name} · {c[labelLang].country}
                  </text>
                </g>
              );
            })()}
          </svg>
        </div>

        {/* City cards grid, accessible fallback (and great for mobile users) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-8">
          {CITIES.map((c) => (
            <div
              key={c.key}
              className="rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm p-3 text-center hover:bg-white/10 transition-colors"
              data-testid={`city-card-${c.key}`}
            >
              <MapPin className="w-4 h-4 text-capsule-aqua mx-auto mb-1" />
              <div className="text-sm font-semibold text-white">{c[labelLang].name}</div>
              <div className="text-xs text-white/60">{c[labelLang].country}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GlobalPresenceMap;
